import React, { useRef } from 'react';
import cx from 'classnames';
import { Highlight } from 'react-instantsearch-dom';

export function HighendCard({ highend, index, openQuickInfoModal }) {
  const showModalButtonRef = useRef();

  async function handleClick() {
    await trackCardClick(highend, index);

    const showModalButtonIsVisible =
      showModalButtonRef.current &&
      showModalButtonRef.current.offsetParent !== null;

    // When the "quick info" button is hidden, we're on mobile, so we directly
    // redirect to the details page, instead of opening the modal.
    if (showModalButtonIsVisible) {
      openQuickInfoModal();
    } else {
      window.location = highend.url;
    }
  }

  const isUnranked = highend.rank.tier === 'Unranked';

  return (
    <div className="highend-card-container">
      <div
        className={cx(
          'highend-card',
          `highend-card-${highend.rank.tier.toLowerCase()}`
        )}
        style={{
          backgroundImage: `url(${highend.image_url})`,
          backgroundPosition: 'right center',
        }}
        onClick={handleClick}
      >
        <div className="text-shadow-strong">
          {/* Rank header */}
          <div className="flex items-center px-3">
            {!isUnranked && (
              <img
                src={`/images/divisions/${highend.rank.tier.toLowerCase()}.png`}
                className="mr-2"
                style={{ width: 70 }}
              />
            )}

            <div>
              <div className="font-semibold">
                {isUnranked
                  ? `UNRANKED`
                  : `RANK: ${highend.rank.tier} ${highend.rank.division}`}
              </div>
              <div>
                {highend.region_name} LEVEL {highend.level}
              </div>
            </div>
          </div>

          <div
            className={cx('flex justify-around', isUnranked ? 'mt-4' : 'mt-1')}
          >
            <div className="flex flex-col items-center">
              <span className="font-semibold">BE</span>
              <span>{highend.blue_essence}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="font-semibold">RP</span>
              <span>{highend.riot_points}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="font-semibold">SKINS</span>
              <span>{highend.skins_count}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="font-semibold">CHAMPS</span>
              <span>{highend.champions_count}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="flex-1 text-center text-shadow-strong mt-4">
          <Highlight hit={highend} attribute="title" tagName="mark" />
        </div>

        {/* CTAs */}
        <div className="hidden lg:flex justify-between mx-4 mt-4 mb-2">
          {/* More info icon */}
          <a
            href={highend.url}
            className="rotated-square-button self-hoverable"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="rotated-square-button__content">
              <i>i</i>
              <span className="rotated-square-button__content-text">
                MORE INFO
              </span>
            </span>
          </a>

          {/* Price */}
          <span className="price font-semibold text-shadow-strong">
            {highend.price.toFixed(2)} USD
          </span>

          {/* Quick info icon */}
          <span
            className="rotated-square-button active-on-parent-hover"
            ref={showModalButtonRef}
          >
            <span className="rotated-square-button__content">
              <span className="rotated-square-button__content-text">
                QUICK INFO
              </span>
              <i>+</i>
            </span>
          </span>
        </div>

        {/* Price (only on mobiles) */}
        <div className="lg:hidden text-center font-semibold my-4">
          {highend.price} USD
        </div>
      </div>
    </div>
  );
}

function trackCardClick({ title, id, price }, position) {
  return new Promise((resolve) => {
    // Fire a "view_item" event as the modal shows the item's details.
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        value: +price,
        currency: 'USD',
        items: [
          {
            item_id: `highend_${id}`,
            item_name: title,
            currency: 'USD',
            index: 0,
            item_category: 'highend',
            price: +price,
            google_business_vertical: 'retail',
          },
        ],
        ecomm_prodid: `highend_${id}`,
        ecomm_pagetype: 'product',
        ecomm_totalvalue: +price,
      },
    });
    resolve();
  });
}
